import { createSlice } from "@reduxjs/toolkit";
import { 
    getAllGeofences,
    getGeofenceByID,
    createGeofence,
    updateGeofence,
    deleteGeofence,
    validateLocation,
    getGeofenceLogs,
    logGeofenceEvent
} from "./thunk";

interface GeofenceState {
    geofences: any[];
    selectedGeofence: any | null;
    geofenceLogs: any[];
    loading: boolean;
    error: string | null;
}

const initialState: GeofenceState = {
    geofences: [],
    selectedGeofence: null,
    geofenceLogs: [],
    loading: false,
    error: null,
};

const geofenceSlice = createSlice({
    name: "geofence",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setSelectedGeofence: (state, action) => {
            state.selectedGeofence = action.payload;
        },
        clearSelectedGeofence: (state) => {
            state.selectedGeofence = null;
        },
    },
    extraReducers: (builder) => {
        // Get all geofences
        builder
            .addCase(getAllGeofences.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllGeofences.fulfilled, (state, action: any) => {
                state.loading = false;
                if (action.payload.status) {
                    state.geofences = action.payload.data || [];
                } else {
                    state.error = action.payload.message || 'Failed to fetch geofences';
                }
            })
            .addCase(getAllGeofences.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to fetch geofences';
            });

        // Get geofence by ID
        builder
            .addCase(getGeofenceByID.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getGeofenceByID.fulfilled, (state, action: any) => {
                state.loading = false;
                if (action.payload.status) {
                    state.selectedGeofence = action.payload.data;
                } else {
                    state.error = action.payload.message || 'Failed to fetch geofence details';
                }
            })
            .addCase(getGeofenceByID.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to fetch geofence details';
            });

        // Create geofence
        builder
            .addCase(createGeofence.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createGeofence.fulfilled, (state, action: any) => {
                state.loading = false;
                if (action.payload.status) {
                    // Refresh the geofences list
                    state.geofences = [...state.geofences, action.payload.data];
                } else {
                    state.error = action.payload.message || 'Failed to create geofence';
                }
            })
            .addCase(createGeofence.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to create geofence';
            });

        // Update geofence
        builder
            .addCase(updateGeofence.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateGeofence.fulfilled, (state, action: any) => {
                state.loading = false;
                if (action.payload.status) {
                    // Update the geofence in the list
                    const index = state.geofences.findIndex(g => g.id === action.payload.data.id);
                    if (index !== -1) {
                        state.geofences[index] = action.payload.data;
                    }
                    state.selectedGeofence = action.payload.data;
                } else {
                    state.error = action.payload.message || 'Failed to update geofence';
                }
            })
            .addCase(updateGeofence.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to update geofence';
            });

        // Delete geofence
        builder
            .addCase(deleteGeofence.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteGeofence.fulfilled, (state, action: any) => {
                state.loading = false;
                if (action.payload.status) {
                    // Remove the geofence from the list
                    state.geofences = state.geofences.filter(g => g.id !== state.selectedGeofence?.id);
                    state.selectedGeofence = null;
                } else {
                    state.error = action.payload.message || 'Failed to delete geofence';
                }
            })
            .addCase(deleteGeofence.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to delete geofence';
            });

        // Get geofence logs
        builder
            .addCase(getGeofenceLogs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getGeofenceLogs.fulfilled, (state, action: any) => {
                state.loading = false;
                if (action.payload.status) {
                    state.geofenceLogs = action.payload.data || [];
                } else {
                    state.error = action.payload.message || 'Failed to fetch geofence logs';
                }
            })
            .addCase(getGeofenceLogs.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to fetch geofence logs';
            });

        // Validate location
        builder
            .addCase(validateLocation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(validateLocation.fulfilled, (state, action) => {
                state.loading = false;
                // Handle location validation response
            })
            .addCase(validateLocation.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to validate location';
            });

        // Log geofence event
        builder
            .addCase(logGeofenceEvent.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(logGeofenceEvent.fulfilled, (state, action) => {
                state.loading = false;
                // Handle geofence event logging response
            })
            .addCase(logGeofenceEvent.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to log geofence event';
            });
    },
});

export const { clearError, setSelectedGeofence, clearSelectedGeofence } = geofenceSlice.actions;
export default geofenceSlice.reducer;
